import { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

type ServerState = 'up' | 'updating' | 'failed';

/**
 * Banner discret affiché pendant les rebuilds serveur.
 * Pas de "Tentative X/Y" stressant — juste un message clair et un retry au clic si échec.
 */
export const ServerStatusBanner = () => {
  const [serverState, setServerState] = useState<ServerState>('up');

  useEffect(() => {
    const handleDown = () => setServerState('updating');
    const handleUp = () => setServerState('up');
    const handleFailed = () => setServerState('failed');

    window.addEventListener('server:down' as any, handleDown);
    window.addEventListener('server:up' as any, handleUp);
    window.addEventListener('server:failed' as any, handleFailed);

    return () => {
      window.removeEventListener('server:down' as any, handleDown);
      window.removeEventListener('server:up' as any, handleUp);
      window.removeEventListener('server:failed' as any, handleFailed);
    };
  }, []);

  const handleRetry = useCallback(() => {
    setServerState('updating');
    // Recharger la page après un court délai pour laisser le banner s'afficher
    setTimeout(() => window.location.reload(), 300);
  }, []);

  if (serverState === 'up') return null;

  return (
    <>
      <div
        className={cn(
          "fixed top-0 left-0 right-0 z-[100]",
          "text-white shadow-lg",
          "px-4 py-3",
          "flex items-center justify-center gap-3",
          "animate-in slide-in-from-top duration-300",
          serverState === 'updating'
            ? "bg-gradient-to-r from-blue-500 to-indigo-500"
            : "bg-gradient-to-r from-amber-500 to-orange-500"
        )}
      >
        {serverState === 'updating' ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" />
            <span className="text-sm font-medium">
              Nous mettons à jour l'outil, veuillez patienter quelques secondes...
            </span>
          </>
        ) : (
          <>
            <span className="text-sm font-medium">
              Le serveur met un peu plus de temps que prévu.
            </span>
            <button
              onClick={handleRetry}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium",
                "bg-white/20 hover:bg-white/30 transition-colors"
              )}
            >
              <RefreshCw className="w-4 h-4" />
              Réessayer
            </button>
          </>
        )}
      </div>
      {/* Spacer */}
      <div className="h-[52px]" aria-hidden="true" />
    </>
  );
};
